import { useState, useCallback, useEffect } from "react";
import { validateNewsForm } from "../../validations/newsForm";
import { useToastPromise } from "../../components/toastPromise";
import { NewsController } from "../../controllers/newsController";
import { Textbox } from "../../components/textbox";
import { Dropdown } from "../../components/dropdown";
import { PopupDialog } from "../../components/popupDialog";
import Button from "@mui/material/Button";
import { NewsStatusList } from "../../constants/newsStatusList";
import { RichText } from "../../components/richText";
import { TableName } from "../../constants/tableName";

export const News = ({
  user,
  isOpen = false,
  handleClose,
  getAll,
  selectedData,
  handlePreviewPopupDialogOpen,
}) => {
  const toastPromise = useToastPromise();
  const emptyHTMLString = "<p>&nbsp;</p>";
  const initialInputs = {
    title: "",
    info: emptyHTMLString,
    status: "",
  };
  const [inputs, setInputs] = useState(initialInputs);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isPreviewDisabled, setIsPreviewDisabled] = useState(true);
  const handleTextChange = (event) => {
    saveInputs(event.target.name, event.target.value);
  };
  const saveInputs = (name, value) => {
    setInputs((values) => ({ ...values, [name]: value }));
  };
  const resetForm = () => {
    setInputs(initialInputs);
    setErrors({});
    setIsPreviewDisabled(true);
  };
  const handleSubmit = (event) => {
    event.preventDefault();
    const newErrors = validateNewsForm(inputs);
    setErrors(newErrors);
    if (Object.keys(newErrors).length !== 0) {
      console.debug("Form submission failed due to validation errors.");
      return;
    }
    console.debug("Form validated successfully.");
    saveNews();
  };
  const saveNews = () => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        selectedData?.id
          ? NewsController.update({
              parameters: {
                ...inputs,
                id: selectedData.id,
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            })
          : NewsController.add({
              parameters: {
                ...inputs,
                createdBy: user.uid,
                createdDate: new Date().toLocaleString(),
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            });
      }),
      successMessage: (data) => {
        console.debug(data);
        return data;
      },
      errorMessage: (data) => data,
    }).finally(() => {
      setLoading(false);
      handleClose();
      resetForm();
      getAll();
    });
  };
  const onHandleChange = useCallback((value, name) => {
    saveInputs(name, value);
  }, []);
  const handleRichTextChange = (event, editor) => {
    const data = editor.getData();
    saveInputs(editor.config.get("name"), data);
    setIsPreviewDisabled(data?.trim() === emptyHTMLString);
  };
  useEffect(() => {
    if (selectedData) {
      setInputs({
        title: selectedData.title,
        info: selectedData.info,
        status: selectedData.status,
      });
      setIsPreviewDisabled(selectedData.info?.trim() === emptyHTMLString);
    }
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  return (
    <PopupDialog
      name="news"
      open={isOpen}
      onClose={() => {
        handleClose();
        resetForm();
      }}
      title={selectedData ? "Update News Detail" : "Add New News Detail"}
      neutralAction={handleClose}
      positiveAction={
        <Button onClick={handleSubmit} disabled={loading}>
          {loading
            ? selectedData
              ? "Updating"
              : "Adding"
            : selectedData
              ? "Update"
              : "Add"}
        </Button>
      }
      negativeAction={
        <Button
          onClick={() => handlePreviewPopupDialogOpen(inputs.info)}
          disabled={isPreviewDisabled}
        >
          Preview
        </Button>
      }
      content={
        <form onSubmit={handleSubmit}>
          {selectedData ? (
            <Textbox
              type="text"
              name="id"
              value={selectedData.id}
              placeholder="ID"
              readOnly={true}
            />
          ) : null}
          <Textbox
            type="text"
            name="title"
            placeholder="Title"
            value={inputs.title}
            onChange={handleTextChange}
            errorMsg={errors.title}
          />
          <Dropdown
            name="status"
            title="Status"
            value={inputs.status}
            options={NewsStatusList}
            onChangeFunc={onHandleChange}
            errorMsg={errors.status}
          />
          <RichText
            name="info"
            onChange={handleRichTextChange}
            placeholder="Info"
            data={inputs.info || emptyHTMLString}
            errorMsg={errors.info}
            tableName={TableName.NEWS}
          />
        </form>
      }
    />
  );
};
